import { Component, ElementRef, HostBinding, Input, Renderer2 } from '@angular/core';
import { format } from 'date-fns';
import {
  isNotNullOrUndefined,
  Inverter,
  System,
  getFirstAndLastMonthDays,
  getFirstAndLastYearDays,
} from 'ngx-core-services';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { ECharts } from 'echarts';
import { map, startWith, switchMap, tap } from 'rxjs/operators';
import { PickerMode } from 'ngx-zcs-components';
import { Store } from '@ngrx/store';

import { EnergySeries, GraphData } from './energy-data-overview.model';
import { buildEnergyPieChart } from './energy-data-overview.graph';
import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import {
  SystemsState,
  requestSystemEnergyDataOverview,
  selectedSystemMetadataNodesType,
  selectedSystemUid,
} from '../../store';
import {
  selectedEnergyDataOverviewRequestStatus,
  selectSystemEnergyStateBySelectedSystem,
} from '../../store/selectors/energy-data-overview.selectors';
import { sumDailyEnergies } from '../utils';

const safePercentage = (part: number, total: number): number => {
  // Avoid division by zero
  if (total <= 0) {
    return 0;
  }
  const value = (part / total) * 100;
  return Math.min(Math.max(value, 0), 100);
}

const getLabels = () => ({
  month: 'Month',
  year: 'Year',
  title: 'Energy overview',
  day: 'Day',
  loading: 'Loading...',
  noDataForTheSelectedPeriod: 'No data for the selected period',
  totalConsumption: 'Total consumption',
  totalProduction: 'Total production',
  todayModeEnabled: 'Today mode enabled',
});
@CreateBlockReduxSupport()
@CreateCustomBlockSizeType({
  'breakpoint-650': 650,
})
@Component({
  selector: 'lib-energy-data-overview',
  templateUrl: './energy-data-overview.component.html',
  styleUrls: ['./energy-data-overview.component.scss'],
})
export class EnergyDataOverviewComponent implements ICustomBlock, IReduxBlock {
  @HostBinding('style.display') display = 'grid';

  readonly _instanceId$: Observable<string> = new Observable<string>();

  readonly selectedMode$ = new BehaviorSubject<PickerMode>('day');
  readonly dateSelection$ = new BehaviorSubject<string>(format(new Date(), 'yyyy-MM-dd'));
  readonly requestStatus$ = this._instanceId$.pipe(
    switchMap((instanceId) => this.store.select(selectedEnergyDataOverviewRequestStatus(instanceId))),
    isNotNullOrUndefined(),
  );
  readonly hideBlock$ = this.store.select(selectedSystemMetadataNodesType).pipe(
    isNotNullOrUndefined(),
    tap((nodes) => {
      this.display = nodes.includes('grid') || nodes.includes('battery') ? 'grid' : 'none';
    }),
  );

  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  @Input() todayMode: boolean | null = false;

  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }

  get labels(): typeof this._labels {
    return this._labels;
  }

  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;

  readonly requestData$ = combineLatest([
    this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined()),
    this.selectedMode$,
    this.dateSelection$,
    this._refresh$,
    this._instanceId$,
  ]).pipe(
    tap(([systemId, mode, date, forceRefresh, instanceId]) => {
      let range;

      if ( this.todayMode ) {
        mode = 'day';
        date = format(new Date(), 'yyyy-MM-dd');
      }

      if(mode === 'day') {
        range = { from: date, to: date };
      } else if (mode === 'month') {
        const weekDays = getFirstAndLastMonthDays(date);
        range = { from: weekDays.from, to: weekDays.to };
      } else {
        const weekDays = getFirstAndLastYearDays(date);
        range = { from: weekDays.from, to: weekDays.to };
      }
      this.store.dispatch(requestSystemEnergyDataOverview({ systemId, instanceId, range, mode, forceRefresh }));
    }),
  );

  readonly requestIsPending$ = this.requestStatus$.pipe(
    map((status) => status === 'pending'),
    startWith(false),
    isNotNullOrUndefined(),
  );

  readonly energyDataOverview$ = this._instanceId$.pipe(
    switchMap((instanceId) => this.store.select(selectSystemEnergyStateBySelectedSystem(instanceId)).pipe(isNotNullOrUndefined()))
  ).pipe(
    map((data) => data.length ? sumDailyEnergies(data) : null),
    map((data) => data && this.adaptEnergyToOverview(data)),
    map((data) => {
      if (!data) {
        return {
          selfConsumeChartOptions: null,
          selfProduceChartOptions: null,
          hasValues: false,
        }
      }

      return {
        selfConsumeChartOptions : buildEnergyPieChart(data.selfConsume, data.consume),
        selfProduceChartOptions : buildEnergyPieChart(data.selfProduction, data.production),
        hasValues: true,
      }
    })
  );

  chartInstances: ECharts[] = [];
  _currentWidth = 0;
  set currentWidth(value: number) {
    if (value === this._currentWidth) {
      return;
    }

    this._currentWidth = value;
    this.chartInstances.forEach((chart) => chart.resize());
  }
  get currentWidth() {
    return this._currentWidth;
  }

  constructor(
    readonly store: Store<SystemsState>,
    readonly el: ElementRef,
    readonly renderer: Renderer2,
  ) { }

  changeSelectMode(mode: string) {
    this.selectedMode$.next(mode as 'day' | 'month' | 'year');
  }

  adaptEnergyToOverview(data: Partial<Inverter>): {
    selfConsume: GraphData;
    selfProduction: GraphData;
    consume: EnergySeries;
    production: EnergySeries;
  } {
    const consumeFromGrid = {
      name: 'fromGrid',
      value: Number(data.EG3 || 0),
      percentage: safePercentage(Number(data.EG3), Number(data.EG4)),
    };

    const consumeFromSolar = {
      name: 'fromSolar',
      value: Number(data.EG4 || 0) - Number(data.EG3 || 0) - Number(data.EG6 || 0),
      percentage: safePercentage(
        Number(data.EG4 || 0) - Number(data.EG3 || 0) - Number(data.EG6 || 0),
        Number(data.EG4)
      ),
    };

    const productionToGrid = {
      name: 'toGrid',
      value: Number(data.EG2 || 0),
      percentage: safePercentage(Number(data.EG2), Number(data.EG1)),
    };

    const fromBattery = {
      name: 'fromBattery',
      value: Number(data.EG6 || 0),
      percentage: safePercentage(Number(data.EG6), Number(data.EG4)),
    };

    const selfProduction = {
      name: 'selfProduction',
      value: Number(data.EG1 || 0) - Number(data.EG2 || 0),
      percentage: safePercentage(
        Number(data.EG1 || 0) - Number(data.EG2 || 0),
        Number(data.EG1)
      ),
    };

    return  {
      selfConsume: [
        consumeFromGrid,
        consumeFromSolar,
        fromBattery,
      ],
      selfProduction: [
        selfProduction,
        productionToGrid,
      ],
      consume: {
        name: this.labels.totalConsumption,
        value: Number(data.EG4),
      },
      production: {
        name: this.labels.totalProduction,
        value: Number(data.EG1),
      }
    }
  }

  onChartInit(ec: ECharts) {
    this.chartInstances.push(ec);
  }
}
